import { createLogger } from '@/lib/logger';
import { ProductsCarousel } from './products-carousel';

export class ItemsYoullLove extends HTMLElement {
  private log = createLogger('tabbed-carousels');
  private tablist: HTMLElement | null = null;
  private tabs: HTMLButtonElement[] = [];
  private panels: HTMLElement[] = [];
  private groupName: string = 'items-youll-love';

  constructor() {
    super();
  }

  connectedCallback() {
    this.hydrate();
  }

  private hydrate() {
    this.groupName = this.getAttribute('data-group') || 'tabbed-carousels';
    this.tablist = this.querySelector<HTMLElement>('.tabbed-carousels--tabs');
    this.tabs = Array.from(
      (this.tablist?.querySelectorAll('.tabbed-carousels--tab') ||
        []) as NodeListOf<HTMLButtonElement>,
    );
    this.panels = Array.from(
      this.querySelectorAll<HTMLElement>('.tabbed-carousels--content-panel'),
    );

    if (!this.tablist || !this.tabs.length || !this.panels.length) {
      this.log.warn('Missing tablist, tabs or panels');
      return;
    }

    this.initTabs();
    this.initSwiper();
  }

  private initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (!document.startViewTransition) {
          this.activateTab(tab);
          return;
        }

        document.startViewTransition(() => {
          this.activateTab(tab);
        });
      });
    });
  }

  private activateTab(tab: HTMLButtonElement) {
    // Deactivate all tabs
    this.tabs.forEach((t) => {
      t.classList.remove('active');
      t.setAttribute('aria-selected', 'false');
    });

    // Activate the given tab
    tab.classList.add('active');
    tab.setAttribute('aria-selected', 'true');
    tab.focus();

    // Identify target panel
    const contentPanelId = tab.getAttribute('data-controls');
    if (!contentPanelId) {
      this.log.warn('No content panel id found on tab', tab);
      return;
    }

    // Update navigations for "Items You'll Love"
    ProductsCarousel.switchVisibleNavigation(this.groupName, contentPanelId);

    const target = this.querySelector<HTMLElement>(`#${CSS.escape(contentPanelId)}`);
    if (!target) {
      this.log.warn(`Content panel with id ${contentPanelId} not found`);
      return;
    }

    // Hide all panels
    this.panels.forEach((p) => {
      p.classList.remove('active');
      p.setAttribute('aria-hidden', 'true');
      p.setAttribute('hidden', '');
    });

    // Show target panel
    target.classList.add('active');
    target.removeAttribute('hidden');
    target.setAttribute('aria-hidden', 'false');

    const tabName = tab.getAttribute('data-controls');
    const activeCarousel = this.querySelector(
      `products-carousel[data-tab="${tabName}"] .swiper`,
    ) as any;
    if (!activeCarousel) {
      this.log.warn('No carousel found for tab', tab);
      return;
    }

    // @ts-ignore
    activeCarousel.swiper?.update?.();
  }

  private initSwiper() {
    // Initialize Swiper on the tabs list (if available)
    // Use element reference rather than global selector to scope to this component
    // @ts-ignore
    new window.Swiper(this.tablist!, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 8,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 80,
      slideToClickedSlide: true,
      breakpoints: {
        768: {
          slideToClickedSlide: true,
          slidesOffsetBefore: 16,
          slidesOffsetAfter: 0,
        },
        1280: {
          slidesPerView: 'auto',
          spaceBetween: 16,
          slideToClickedSlide: false,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
      },
      on: {
        resize(swiper: any) {
          swiper.slideToClosest();
        },
      },
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('tabbed-carousels')) {
    customElements.define('tabbed-carousels', ItemsYoullLove);
  }
});
